import React, { useCallback, useEffect, useState } from 'react';
import { FaPlus, FaEdit, FaTrash, FaVideo, FaTimes, FaThLarge } from 'react-icons/fa';
import { AD_FORMS, DURATION_PRESETS, PLACEMENT_LABELS, formatMoney } from './shopConstants';

const EMPTY_FORM = {
    name: '',
    price: '',
    durationDays: 30,
    form: 'home_header',
    allowVideo: false,
    badgeLabel: '',
    description: '',
    isActive: true,
    sortOrder: 0,
};

const PackageForm = ({ initial, busy, onSave, onCancel }) => {
    const [form, setForm] = useState(initial);
    const formInfo = AD_FORMS.find((f) => f.value === form.form);

    return (
        <div className="shops-modal-overlay" onClick={onCancel}>
            <form className="shops-modal" onClick={(e) => e.stopPropagation()} onSubmit={(e) => { e.preventDefault(); onSave(form); }}>
                <button type="button" className="modal-close" onClick={onCancel} aria-label="إغلاق"><FaTimes /></button>
                <h2>{initial._id ? 'تعديل الباقة' : 'باقة إعلانية جديدة'}</h2>
                <div className="form-grid">
                    <label>اسم الباقة<input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required placeholder="مثلاً: الباقة الذهبية" /></label>
                    <label>شارة (اختياري)<input value={form.badgeLabel} onChange={(e) => setForm({ ...form, badgeLabel: e.target.value })} placeholder="الأكثر طلباً" /></label>
                    <label>السعر (د.ل)<input type="number" min="0" step="0.5" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} required /></label>
                    <label>المدة (أيام)<input type="number" min="1" step="1" value={form.durationDays} onChange={(e) => setForm({ ...form, durationDays: e.target.value })} required /></label>
                </div>
                <div className="duration-presets">
                    {DURATION_PRESETS.map((p) => (
                        <button type="button" key={p.days} className={Number(form.durationDays) === p.days ? 'active' : ''} onClick={() => setForm({ ...form, durationDays: p.days })}>
                            {p.label}
                        </button>
                    ))}
                </div>
                <div className="field-label">نوع الإعلان</div>
                <div className="placement-options">
                    {AD_FORMS.map((f) => (
                        <label key={f.value} className={`placement-option ${form.form === f.value ? 'active' : ''}`}>
                            <input type="radio" name="ad-form" checked={form.form === f.value} onChange={() => setForm({ ...form, form: f.value })} />
                            {f.label}
                        </label>
                    ))}
                </div>
                {formInfo && <div className="muted small">{formInfo.hint}{formInfo.limited ? ' · عدد المساحات محدود (من إعدادات المساحات)' : ''}</div>}
                <label className="field-label">المميزات (سطر لكل ميزة)
                    <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder={'ظهور أعلى الصفحة الرئيسية\nأولوية في قائمة الورش'} />
                </label>
                <div className="toggles">
                    <label className="inline-check"><input type="checkbox" checked={form.allowVideo} onChange={(e) => setForm({ ...form, allowVideo: e.target.checked })} /> السماح بإعلانات الفيديو</label>
                    <label className="inline-check"><input type="checkbox" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} /> متاحة للشراء</label>
                    <label className="inline-number">الترتيب<input type="number" value={form.sortOrder} onChange={(e) => setForm({ ...form, sortOrder: e.target.value })} /></label>
                </div>
                <div className="muted small">تعديل الباقة لا يغيّر الإعلانات التي تم شراؤها مسبقاً.</div>
                <div className="modal-actions">
                    <button type="submit" className="btn primary" disabled={busy || !form.form}>حفظ</button>
                    <button type="button" className="btn ghost" onClick={onCancel}>إلغاء</button>
                </div>
            </form>
        </div>
    );
};

// How many ads of each limited form can run at the same time (whole country).
const SpotsCard = ({ actions }) => {
    const [capacity, setCapacity] = useState(null);
    useEffect(() => {
        actions.getAdCapacity().then((json) => json && setCapacity(json.capacity));
    }, [actions.getAdCapacity]); // eslint-disable-line react-hooks/exhaustive-deps
    if (!capacity) return null;
    const save = async () => {
        const json = await actions.saveAdCapacity({ home_header: Number(capacity.home_header), waiting_offers: Number(capacity.waiting_offers) });
        if (json) setCapacity(json.capacity);
    };
    return (
        <div className="spots-card">
            <div className="spots-title"><FaThLarge /> عدد المساحات الإعلانية في نفس الوقت</div>
            <div className="spots-fields">
                {AD_FORMS.filter((f) => f.limited).map((f) => (
                    <label key={f.value}>
                        {f.label}
                        <input type="number" min="1" max="50" value={capacity[f.value] ?? ''} onChange={(e) => setCapacity({ ...capacity, [f.value]: e.target.value })} />
                    </label>
                ))}
                <button type="button" className="btn primary" disabled={actions.busy} onClick={save}>حفظ</button>
            </div>
            <div className="muted small">عند امتلاء المساحات يرى صاحب الورشة أقرب موعد متاح ويحجزه. تقليل العدد لا يوقف الإعلانات المحجوزة. الورش الموثوقة بدون حد.</div>
        </div>
    );
};

const PackagesPanel = ({ actions }) => {
    const [packages, setPackages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [deleting, setDeleting] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        const json = await actions.listPackages();
        if (json) setPackages(json);
        setLoading(false);
    }, [actions.listPackages]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load]);

    const save = async (form) => {
        const body = {
            ...form,
            price: Number(form.price),
            durationDays: Number(form.durationDays),
            sortOrder: Number(form.sortOrder) || 0,
            description: String(form.description || '').split('\n').map((l) => l.trim()).filter(Boolean),
        };
        if (await actions.savePackage(form._id, body)) {
            setEditing(null);
            load();
        }
    };

    const remove = async (id) => {
        if (await actions.deletePackage(id)) {
            setDeleting(null);
            load();
        }
    };

    return (
        <div className="panel">
            <div className="panel-toolbar">
                <div className="muted">الباقات التي يشتريها أصحاب الورش من رصيدهم لعرض إعلاناتهم.</div>
                <button type="button" className="btn primary" onClick={() => setEditing({ ...EMPTY_FORM })}><FaPlus /> باقة جديدة</button>
            </div>
            <SpotsCard actions={actions} />
            {loading ? (
                <div className="loading">جاري التحميل...</div>
            ) : packages.length === 0 ? (
                <div className="empty-state">لا توجد باقات بعد. أضف أول باقة ليتمكن أصحاب الورش من الإعلان.</div>
            ) : (
                <div className="packages-grid">
                    {packages.map((pkg) => (
                        <article key={pkg._id} className={`package-card ${pkg.isActive ? '' : 'inactive'}`}>
                            {pkg.badgeLabel && <span className="package-badge">{pkg.badgeLabel}</span>}
                            <h3>{pkg.name}</h3>
                            {pkg.isLegacy && <div className="legacy-note">باقة قديمة (عدة أماكن) - مخفية عن أصحاب الورش. أنشئ بدلها باقات بنوع واحد.</div>}
                            <div className="package-price">{formatMoney(pkg.price)}<span> / {pkg.durationDays} يوم</span></div>
                            <div className="chips">
                                {pkg.placements.map((p) => <span key={p} className="chip small">{PLACEMENT_LABELS[p] || p}</span>)}
                                {pkg.allowVideo && <span className="chip small chip-accent"><FaVideo /> فيديو</span>}
                            </div>
                            {pkg.description?.length > 0 && (
                                <ul className="package-features">{pkg.description.map((d) => <li key={d}>{d}</li>)}</ul>
                            )}
                            <div className="package-sales">
                                <div><strong>{pkg.soldCount}</strong><span>مبيعات</span></div>
                                <div><strong>{formatMoney(pkg.revenue)}</strong><span>إيرادات</span></div>
                            </div>
                            {!pkg.isActive && <div className="muted small">غير متاحة للشراء حالياً</div>}
                            {deleting === pkg._id ? (
                                <div className="modal-actions">
                                    <button type="button" className="btn danger" onClick={() => remove(pkg._id)}>تأكيد الحذف</button>
                                    <button type="button" className="btn ghost" onClick={() => setDeleting(null)}>إلغاء</button>
                                </div>
                            ) : (
                                <div className="modal-actions">
                                    <button type="button" className="btn ghost" onClick={() => setEditing({ ...pkg, form: pkg.form || 'home_header', description: (pkg.description || []).join('\n') })}><FaEdit /> تعديل</button>
                                    <button type="button" className="btn ghost danger-text" onClick={() => setDeleting(pkg._id)}><FaTrash /> حذف</button>
                                </div>
                            )}
                        </article>
                    ))}
                </div>
            )}
            {editing && <PackageForm initial={editing} busy={actions.busy} onSave={save} onCancel={() => setEditing(null)} />}
        </div>
    );
};

export default PackagesPanel;
